// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import React, {useCallback, useEffect} from 'react';
import {
    FormField,
    SpaceBetween,
    Button,
    Form,
    Select,
    Multiselect,
} from '@cloudscape-design/components';
import {useHistory} from 'react-router-dom';
import {CREATE_DIAGRAM} from '../../../../../routes';
import {useDiagramSettingsState} from '../../../../Contexts/DiagramSettingsContext';
import {
    diagramsPrefix,
    useListObjects,
    usePutObject,
} from '../../../../Hooks/useS3Objects';
import * as R from 'ramda';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {useGetAccountResourceGraph} from '../../../../Hooks/useGetResourceGraph';
import {useResourceState} from '../../../../Contexts/ResourceContext';
import {useResourcesMetadata} from '../../../../Hooks/useResourcesMetadata';
import {fetchImage} from '../../../../../Utils/ImageSelector';

dayjs.extend(relativeTime);

const mapIndexed = R.addIndex(R.map);

const CreateAccountDiagramNameForm = ({direction, accounts}) => {
    const [{canvas}, dispatchCanvas] = useDiagramSettingsState();
    const [{graphResources}, dispatch] = useResourceState();
    const [name, setName] = React.useState('');
    const [resourceIds, setResourceIds] = React.useState([]);
    const [account, setAccount] = React.useState({
        label: 'Select an account',
        value: null,
    });
    const [visibility, setVisibility] = React.useState({
        label: 'Private',
        value: 'private',
    });
    const {data = [], refetch} = useListObjects(
        diagramsPrefix,
        visibility.value
    );
    const {putAsync} = usePutObject(diagramsPrefix);

    const {data: resourcesMetadata, status} = useResourcesMetadata();

    const resourceTypes = (resourcesMetadata?.resourceTypes ?? []).map(
        x => x.type
    );

    const [selectedResourceTypes, setSelectedResourceTypes] =
        React.useState(null);

    // Use all available resource types from the system, not hardcoded list
    const allAvailableResourceTypes = resourceTypes.map(type => ({
        label: type,
        value: type,
        iconUrl: fetchImage(type),
    }));

    const {
        data: accountData,
        refetch: loadAccount,
        isLoading,
        isError,
    } = useGetAccountResourceGraph(
        account.accountId,
        (selectedResourceTypes ?? allAvailableResourceTypes).map(i => i.value)
    );

    const history = useHistory();

    useEffect(() => {
        if (accountData && !isError) {
            console.log('📊 Processing accountData:', accountData);
            const nodes = accountData.filter(x => x.group === 'nodes');

            const nodeSet = new Set(nodes.map(n => n.data.id));

            const edges = accountData.filter(x => {
                return (
                    x.group === 'edges' &&
                    nodeSet.has(x.data.source) &&
                    nodeSet.has(x.data.target)
                );
            });

            const accountNode = nodes.find(n => n.data.type === 'account');

            if (accountNode != null) {
                const accountInfo = accounts.find(
                    a => a.accountId === account.accountId
                );
                accountNode.data.label = `${accountNode.data.label} - ${accountInfo.name}`;
            }

            console.log('🎨 Final diagram data:', {nodes, edges});

            Promise.resolve(
                dispatch({
                    type: 'updateGraphResources',
                    graphResources: [...nodes, ...edges],
                })
            ).then(() => history.push(CREATE_DIAGRAM));
        }
    }, [accountData, dispatch, history, isError, accounts, account.accountId]);

    const diagrams = mapIndexed((e, index) => {
        return {
            id: index,
            name: e.key.split('/')[e.key.split('/').length - 1],
            modified: e.lastModified,
        };
    })(data);

    const handleCreate = () => {
        console.log('🚀 Creating account diagram for:', account.accountId);
        return loadAccount();
    };

    const diagramExists = () =>
        R.filter(e => R.equals(e.name, name), diagrams).length > 0;

    const isValidForm = () => !(account.value == null);

    return (
        <Form
            actions={
                <Button
                    iconAlign="right"
                    onClick={handleCreate}
                    disabled={!isValidForm() || isLoading}
                    variant={diagramExists() ? 'normal' : 'primary'}
                >
                    {diagramExists() ? 'Overwrite' : 'Add to diagram'}
                </Button>
            }
        >
            <SpaceBetween direction={direction} size="l">
                <FormField label="Visibility">
                    <Select
                        selectedOption={visibility}
                        onChange={({detail}) =>
                            setVisibility(detail.selectedOption)
                        }
                        options={[
                            {label: 'Private', value: 'private'},
                            {label: 'Public', value: 'public'},
                        ]}
                        selectedAriaLabel="Selected"
                    />
                </FormField>
                <FormField label="Account">
                    <Select
                        selectedOption={account}
                        onChange={({detail}) =>
                            setAccount(detail.selectedOption)
                        }
                        options={accounts.map(x => {
                            return {
                                accountId: x.accountId,
                                value: x.accountId,
                                label: x.name,
                                description: x.accountId,
                            };
                        })}
                        filteringType="auto"
                    />
                </FormField>
                <FormField
                    label={'Resource Types'}
                    description={'Select which resource types to display'}
                >
                    <Multiselect
                        placeholder="Select resource types"
                        onChange={e => {
                            setSelectedResourceTypes(e.detail.selectedOptions);
                        }}
                        options={[
                            {
                                label: 'Select All',
                                options: resourceTypes.map(type => ({
                                    label: type,
                                    value: type,
                                    iconUrl: fetchImage(type),
                                })),
                            },
                        ]}
                        filteringType={'auto'}
                        selectedOptions={
                            selectedResourceTypes ?? allAvailableResourceTypes
                        }
                        selectedAriaLabel="Selected"
                        expandToViewport
                        tokenLimit={6}
                        statusType={status}
                    />
                </FormField>
            </SpaceBetween>
        </Form>
    );
};

export default CreateAccountDiagramNameForm;
